import { type GeoLocation } from "@src/appV2/Location";
import {
  type ClaimShiftRequest,
  useClaimShift,
} from "@src/appV2/OpenShifts/ShiftAction/api/useClaimShift";
import { useShiftDiscoverySearchMode } from "@src/appV2/redesign/ShiftDiscovery/useShiftDiscoverySearchMode";
import { useCallback, useState } from "react";

interface FailedShift {
  shiftId: string;
  error: unknown;
}

interface BookDoubleShiftsResult {
  bookedShiftIds: string[];
  failedShifts: FailedShift[];
}

interface ShiftToBook {
  shiftId: string;
  offerId: string;
}

export function useBookDoubleShifts() {
  const { mutateAsync: claimShift } = useClaimShift();
  const searchMode = useShiftDiscoverySearchMode();
  const [isBooking, setIsBooking] = useState(false);

  const bookDoubleShifts = useCallback(
    async (shifts: ShiftToBook[], geoLocation?: GeoLocation): Promise<BookDoubleShiftsResult> => {
      setIsBooking(true);

      try {
        const claimRequests: ClaimShiftRequest[] = shifts.map((shift) => ({
          shiftId: shift.shiftId,
          offerId: shift.offerId,
          searchMode,
          latitude: geoLocation?.latitude,
          longitude: geoLocation?.longitude,
        }));

        const bookedShiftIds: string[] = [];
        const failedShifts: FailedShift[] = [];

        // Book shifts sequentially to avoid backend lock conflicts when
        // two claims fire concurrently for the same worker.
        for (const request of claimRequests) {
          try {
            await claimShift(request);
            bookedShiftIds.push(request.shiftId);
          } catch (error: unknown) {
            failedShifts.push({ shiftId: request.shiftId, error });
          }
        }

        return { bookedShiftIds, failedShifts };
      } finally {
        setIsBooking(false);
      }
    },
    [claimShift, searchMode]
  );

  return {
    bookDoubleShifts,
    isBooking,
  };
}
